import * as XLSX from "xlsx-js-style"; // <-- changed from "xlsx"
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

interface ExportAntdColumnsProps {
  columns: any[];
  dataSource: Record<string, any>[];
  fileName?: string;
  sheetName?: string;
}

export const exportAntdTableToExcel = ({
  columns,
  dataSource,
  fileName = "Report",
  sheetName = "Sheet1",
}: ExportAntdColumnsProps): Promise<void> => {
   return new Promise((resolve) => {
    if (!Array.isArray(dataSource) || dataSource.length === 0) {
      toast.warn("No data available to export.");
      resolve();
      return;
    }

  // pick only columns that map to a real field, skip Action/S.No etc
  const exportColumns = columns.filter(
    (col: any) => col.dataIndex && col.excludeFromExport !== true
  );

  const formattedData = dataSource.map((record, index) => {
    const row: Record<string, any> = {};

    exportColumns.forEach((col: any) => {
      const header = typeof col.title === "string" ? col.title : col.key ?? col.dataIndex;
      const rawValue = record[col.dataIndex];

      row[header] = col.excelFormatter
        ? col.excelFormatter(rawValue, record, index)
        : rawValue ?? "";
    });

    return row;
  });

  const worksheet = XLSX.utils.json_to_sheet(formattedData);

  worksheet["!cols"] = exportColumns.map((col: any) => ({
    wch: Math.max((typeof col.title === "string" ? col.title.length : 8) + 1, 10),
  }));

  // --- style the header row (row 0) ---
  const headerStyle = {
    fill: { fgColor: { rgb: "1F3864" } },
    font: { bold: true, color: { rgb: "FFFFFF" } },
    alignment: { vertical: "center" },
    // alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    },
  };

  const headerRange = XLSX.utils.decode_range(worksheet["!ref"]!);
  for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    if (worksheet[cellAddress]) {
      worksheet[cellAddress].s = headerStyle;
    }
  }
  // --- end header styling ---

  // --- style the Sr No. column (center or left alignment) ---
  const srNoColumn = exportColumns.find((col: any) =>
    col.dataIndex === "sr_no" || col.title === "Sr No."
  );

  if (srNoColumn) {
    const srNoColIndex = exportColumns.indexOf(srNoColumn);
    const dataRowStyle = {
      alignment: { horizontal: "center", vertical: "center" }, // Change to "left" as needed
      border: {
        top: { style: "thin", color: { rgb: "D3D3D3" } },
        bottom: { style: "thin", color: { rgb: "D3D3D3" } },
        left: { style: "thin", color: { rgb: "D3D3D3" } },
        right: { style: "thin", color: { rgb: "D3D3D3" } },
      },
    };

    for (let row = 1; row <= headerRange.e.r; row++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: srNoColIndex });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = dataRowStyle;
      }
    }
  }
  // --- end Sr No. column styling ---

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${fileName}.xlsx`);
    resolve(); // Resolve after saveAs
  });
};